/**
 * Frame clock. Time is divided into fixed slots of 1/fps starting at an epoch;
 * a Worker timer wakes the main thread once per slot. Deadlines are derived
 * from the epoch (never from the previous wake-up), so timer jitter does not
 * accumulate into drift.
 */

/** A tick may arrive this fraction of an interval before its deadline and still belong to that slot. */
const EARLY_TOLERANCE = 0.2;
/** A frame may run this fraction of an interval into the next slot before that slot is given up. */
const OVERRUN_TOLERANCE = 0.25;

export class FrameClock {
  intervalMs = 1000 / 30;
  epochMs = 0;
  lastSlot = -1;
  skippedFrames = 0;
  /** The part of skippedFrames that was not caused by a slow frame: nothing ran the tick in time. */
  starvedFrames = 0;
  lateFrames = 0;
  private busyUntilMs = -Infinity;

  reset(epochMs: number, fps: number): void {
    this.epochMs = epochMs;
    this.intervalMs = 1000 / Math.max(1, fps);
    this.lastSlot = -1;
    this.busyUntilMs = -Infinity;
  }

  /** Records when composing the current frame finished. */
  frameDone(nowMs: number): void {
    this.busyUntilMs = nowMs;
  }

  slotAt(nowMs: number): number {
    return Math.floor((nowMs - this.epochMs) / this.intervalMs + EARLY_TOLERANCE);
  }

  deadline(slot: number): number {
    return this.epochMs + slot * this.intervalMs;
  }

  /**
   * Called for every timer wake-up. Returns the slot to compose, or -1 when
   * this wake-up must be ignored: its slot was already composed, or the
   * previous frame overran well into it. In the second case the slot is given
   * up and composition resumes at the next deadline, which keeps the cadence
   * regular and leaves the thread idle for the rest of the slot instead of
   * composing back to back under overload.
   */
  tick(nowMs: number): number {
    const slot = this.slotAt(nowMs);
    if (slot <= this.lastSlot || slot < 0) return -1;
    if (this.busyUntilMs - this.deadline(slot) > this.intervalMs * OVERRUN_TOLERANCE) return -1;
    if (this.lastSlot >= 0 && slot > this.lastSlot + 1) {
      this.skippedFrames += slot - this.lastSlot - 1;
      // Missed slots that began after the previous frame had finished were lost to the thread
      // not running (blocked or throttled), not to composing taking too long.
      const firstFree = Math.ceil((this.busyUntilMs - this.epochMs) / this.intervalMs - OVERRUN_TOLERANCE);
      this.starvedFrames += Math.max(0, slot - Math.max(this.lastSlot + 1, firstFree));
    }
    if (nowMs - this.deadline(slot) > this.intervalMs / 2) this.lateFrames++;
    this.lastSlot = slot;
    return slot;
  }
}

// Runs in a dedicated Worker so the cadence survives background-tab timer
// throttling. `at` is on the shared wall clock (timeOrigin + now), which lets
// the worker align its deadlines with the main thread's epoch.
const WORKER_SOURCE = `
let timer = 0, epoch = 0, interval = 0, slot = 0;
function arm() {
  const now = performance.now();
  slot = Math.max(slot + 1, Math.ceil((now - epoch) / interval));
  timer = setTimeout(fire, epoch + slot * interval - now);
}
function fire() { postMessage(slot); arm(); }
onmessage = (e) => {
  clearTimeout(timer);
  if (e.data.type !== 'start') return;
  epoch = e.data.at - performance.timeOrigin;
  interval = e.data.interval;
  slot = -1;
  arm();
};
`;

export class WorkerTicker {
  private worker: Worker | null = null;
  private url: string | null = null;

  constructor(
    private readonly onTick: () => void,
    private readonly onError: (message: string) => void = () => undefined,
  ) {}

  /** `epochMs` is a main-thread performance.now() value. */
  start(epochMs: number, intervalMs: number): void {
    if (!this.worker) {
      this.url = URL.createObjectURL(new Blob([WORKER_SOURCE], { type: 'text/javascript' }));
      this.worker = new Worker(this.url, { name: 'studio-frame-clock' });
      this.worker.onmessage = () => this.onTick();
      this.worker.onerror = (event) => this.onError(event.message || 'worker error');
    }
    this.worker.postMessage({ type: 'start', at: performance.timeOrigin + epochMs, interval: intervalMs });
  }

  stop(): void {
    this.worker?.postMessage({ type: 'stop' });
  }

  destroy(): void {
    if (this.worker) {
      this.worker.onmessage = null;
      this.worker.onerror = null;
      this.worker.terminate();
      this.worker = null;
    }
    if (this.url) {
      URL.revokeObjectURL(this.url);
      this.url = null;
    }
  }
}
